import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  useActionSets,
  useAutoTransactionRules,
  useMatchSets,
} from "../../api/queries";
import type {
  AutoTransactionRuleDto,
  InvoiceId,
  RuleProblemDto,
} from "../../api/types";
import { describeProblem, isUnreadable } from "../../logic/rule-problems";
import { invoicePath } from "../../logic/invoice-path";

/**
 * Dead rules and broken library entries, surfaced on the inbox.
 *
 * A rule naming a hidden entity — or citing a library entry that no longer
 * exists — is refused by the engine: it neither matches nor applies, because
 * assigning a removed group would write a categorization the registry no longer
 * permits. So the question this answers is "I removed something; what needs
 * updating?", and it is grouped by the *problem* rather than by the rule,
 * because that is the shape of the work: one hidden category means one decision
 * and N rules to edit.
 */
type Affected = { id: string; name: string; to: string };

const key = (problem: RuleProblemDto) => describeProblem(problem);

/** The invoice a virtual rule was generated from: its leading
 * `invoiceRuleApplied` action names it. (The rule's own id is derived from the
 * same ULID, but the backend keeps that only as a display key.) */
const generatingInvoice = (rule: AutoTransactionRuleDto): InvoiceId | null =>
  rule.actions.flatMap((action) =>
    action.field === "invoiceRuleApplied" ? [action.value.invoice] : [],
  )[0] ?? null;

export const RuleHealthWarnings = () => {
  const { data: rules } = useAutoTransactionRules();
  const { data: matchSets } = useMatchSets();
  const { data: actionSets } = useActionSets();

  const byProblem = new Map<string, Affected[]>();
  const add = (problems: RuleProblemDto[], entry: Affected) => {
    for (const problem of problems) {
      const list = byProblem.get(key(problem)) ?? [];
      list.push(entry);
      byProblem.set(key(problem), list);
    }
  };

  for (const rule of rules ?? []) {
    const invoice = generatingInvoice(rule);
    add(rule.problems, {
      id: rule.id,
      name: rule.name,
      // A virtual rule is generated from an invoice and has no editor of its
      // own — the fix is on the invoice, so the link opens that invoice's view.
      // An unreadable stub has no editor either; its fix (delete) is on the
      // list.
      to: rule.editable
        ? `/admin/auto-transactions/${rule.id}`
        : isUnreadable(rule.problems)
          ? "/admin/auto-transactions"
          : invoice
            ? invoicePath(invoice)
            : "/admin/invoices",
    });
  }
  for (const set of matchSets ?? [])
    add(set.problems, { id: set.id, name: set.name, to: "/admin/library" });
  for (const set of actionSets ?? [])
    add(set.problems, { id: set.id, name: set.name, to: "/admin/library" });

  if (byProblem.size === 0) return null;

  const total = new Set([...byProblem.values()].flat().map((entry) => entry.id))
    .size;

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>
        {/* Rules and library entries are counted together — a broken shared
            entry is as much a thing to fix as a broken rule, and splitting the
            count would bury it. */}
        {total === 1
          ? "1 rule or library entry can't run until it's updated"
          : `${total} rules and library entries can't run until they're updated`}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
        These reference something that has been removed, so they no longer match
        or categorize anything. Fix or delete them.
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {[...byProblem.entries()].map(([problem, affected]) => (
          <Box key={problem}>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {problem}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary", ml: 1 }}
            >
              {affected.map((entry, index) => (
                <Box component="span" key={entry.id}>
                  {index > 0 && ", "}
                  <Link to={entry.to}>{entry.name}</Link>
                </Box>
              ))}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Alert>
  );
};
